/* eslint-disable react-hooks/exhaustive-deps */
import { ClearableSearchInput } from "components/SearchField/SearchField";
import axios from "axios";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import BillingModal from "components/displayModel/billingModel";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import ListingActionMenu from "components/ListingActionMenu";
import Skeleton from "components/Skeleton/Skeleton";
import CardSkeleton from "components/Skeleton/CardSkeleton";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiEdit2,
  FiEye,
  FiMoreHorizontal,
  FiPlus,
  FiSearch,
  FiUpload,
  FiTrash2,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteExpenses, fetchedExpenses, getCurrencyCode } from "utils/api";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { getScreenRight, showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import useFeatureAccess from "utils/useFeatureAccess";
import { expenseReceiptAccept, expenseReceiptError } from "utils/expenseReceipt";
import "./expenses.css";
import "styles/register-page-layout.css";

const SCREEN_NAME = "Expenses";
const formatExpenseDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value || "—"
    : date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};
const expenseMonthKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const expenseMonthLabel = (value) => {
  if (!value) return "All";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });
};

function Expenses() {
  const receiptInput = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const featureAccess = useFeatureAccess();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const perPage = appConstants.limit || 10;
  const [screenAccess, setScreenAccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [activeTab, setActiveTab] = useState("saved");
  const [openMenu, setOpenMenu] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("AUD");
  const [receiptViewer, setReceiptViewer] = useState(null);
  const [itemCategoriesName, setItemCategoriesName] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [registerFilters, setRegisterFilters] = useState({
    receipt: "All",
    payment: "All",
    month: "",
  });
  const [values, setValues] = useState({
    sortingType: "",
    sortingField: "",
    name: "All",
    categoryCodeName: "All",
    page: appConstants.page,
    limit: appConstants.limit,
    filterValues: "",
    searchParameterList: [
      "categoryCodeName",
      "name",
      "expenseDate",
      "totalAmount",
      "taxAmount",
      "tipAmount",
      "notes",
      "companyName",
    ],
    businessUserID: JSON.parse(sessionStorage.getItem("LOGIN_DETAILS"))
      ?.businessUserID,
  });

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    await fetchedExpenses(
      values,
      (data) => {
        setTotalRows(data?.Result_Output?.totalDocs || 0);
        setRows(data?.Result_Output?.docs || []);
      },
      dispatch
    );
    setLoading(false);
  }, [dispatch, values]);

  useEffect(() => {
    let active = true;
    const businessPayload = { businessUserID: values.businessUserID };

    Promise.all([
      getScreenRight(SCREEN_NAME).then((right) => {
        if (active) setScreenAccess(right === "Full Access");
      }),
      getCurrencyCode(
        businessPayload,
        (data) => {
          if (active && data.Result_Status)
            setCurrencyCode(data.Result_Output || "AUD");
        },
        dispatch
      ),
      axios
        .post(`${appConstants.baseURL}items/fetchCategoriesData`, businessPayload)
        .then((res) => {
          const categories = Array.from(
            new Set(
              (res.data.fetchCategories || [])
                .map((item) => item.categoryCodeName)
                .filter(Boolean)
            )
          );
          if (active) setItemCategoriesName(categories);
        })
        .catch(() => {
          if (active) setItemCategoriesName([]);
        }),
    ]).catch(() => undefined);

    return () => {
      active = false;
    };
  }, [dispatch, values.businessUserID]);

  useEffect(() => {
    if (!values.filterValues) fetchExpenses();
  }, [fetchExpenses, values.filterValues]);

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        const hasReceipt = Boolean(
          row.uploadDocument || row.receipt || row.receiptFileName
        );
        const isDraft =
          row.status?.toLowerCase() === "draft" || row.isDraft === true;
        if (activeTab === "missing" && hasReceipt) return false;
        if (activeTab === "drafts" && !isDraft) return false;
        if (activeTab === "saved" && isDraft) return false;
        if (
          registerFilters.receipt !== "All" &&
          (registerFilters.receipt === "Attached" ? !hasReceipt : hasReceipt)
        )
          return false;
        if (
          registerFilters.payment !== "All" &&
          (row.paymentStatus || "Not confirmed") !== registerFilters.payment
        )
          return false;
        if (
          registerFilters.month &&
          expenseMonthKey(row.expenseDate) !== registerFilters.month
        )
          return false;
        return true;
      }),
    [activeTab, registerFilters, rows]
  );
  const expenseMonths = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => expenseMonthKey(row.expenseDate)).filter(Boolean)))
        .sort()
        .reverse(),
    [rows]
  );
  const hasLocalRegisterFilters =
    registerFilters.receipt !== "All" ||
    registerFilters.payment !== "All" ||
    Boolean(registerFilters.month) ||
    activeTab !== "saved";
  const displayedRowsCount = hasLocalRegisterFilters
    ? visibleRows.length
    : totalRows;
  const savedRows = rows.filter(
    (row) => row.status?.toLowerCase() !== "draft" && row.isDraft !== true
  );
  const recordedSpend = savedRows.reduce(
    (sum, row) => sum + (Number(row.totalAmount) || 0),
    0
  );
  const gstRecorded = savedRows.reduce(
    (sum, row) => sum + (Number(row.taxAmount) || 0),
    0
  );
  const missingReceipts = savedRows.filter(
    (row) => !(row.uploadDocument || row.receipt || row.receiptFileName)
  ).length;
  const pageCount = Math.max(1, Math.ceil(totalRows / perPage));
  const loadingRows = Array.from({ length: 6 }, (_, index) => index);

  const handleAddNew = () => {
    if (!featureAccess) return setShowBillingModal(true);
    if (!screenAccess)
      return showResMsg(genericMessage.notPermission, 2000, false);
    return navigate("/expenses/addExpenses");
  };
  const handleReceiptUpload = () => {
    if (!featureAccess) return setShowBillingModal(true);
    if (!screenAccess) return showResMsg(genericMessage.notPermission, 2000, false);
    receiptInput.current?.click();
  };
  const importReceipt = (event) => {
    const receiptFile = event.target.files?.[0];
    event.target.value = "";
    if (!receiptFile) return;
    const error = expenseReceiptError(receiptFile);
    if (error) return showResMsg(error, 2000, false);
    if (!featureAccess || !screenAccess) return;
    navigate("/expenses/addExpenses", { state: { receiptFile } });
  };
  const requestDelete = (id) => {
    if (!screenAccess)
      return showResMsg(genericMessage.notPermission, 2000, false);
    setOpenMenu("");
    setMenuAnchorEl(null);
    setDeleteId(id);
    setDeleteDialogOpen(true);
    return undefined;
  };
  const handleDelete = async () => {
    await deleteExpenses(
      { ...values, _id: deleteId },
      (data) => {
        showResMsg(
          data.Result_Status ? genericMessage.deleted : data.Result_Message,
          1000,
          data.Result_Status
        );
        fetchExpenses();
      },
      dispatch
    );
    setDeleteDialogOpen(false);
  };
  const exportRows = () => {
    const headings = [
      "Expense date",
      "Supplier",
      "Account",
      "Tax amount",
      "Total amount",
      "Outstanding",
    ];
    const data = visibleRows.map((row) => [
      row.expenseDate,
      row.companyName || row.name,
      row.categoryCodeName,
      row.taxAmount,
      row.totalAmount,
      row.paymentStatus === "Not confirmed" ? "" : row.outstandingAmount,
    ]);
    const csv = [headings, ...data]
      .map((line) =>
        line
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    link.download = "expenses.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const changePage = (page) => {
    if (page >= 1 && page <= pageCount)
      setValues((current) => ({ ...current, page }));
  };

  return (
    <DashboardLayout>
      <main
        className={`app-register-page-content expenses-page${
          darkMode ? " expenses-page--dark" : ""
        }`}
      >
        <header className="expenses-hero">
          <div>
            <h1>Expenses</h1>
            <p>Track business expenses, receipts, and tax details.</p>
          </div>
          <div className="expenses-hero__actions">
            <button
              className="expenses-button expenses-button--secondary"
              type="button"
              onClick={exportRows}
            >
              <FiDownload />
              Export
            </button>
            <input ref={receiptInput} type="file" hidden aria-label="Import expense receipt" accept={expenseReceiptAccept} onChange={importReceipt} />
            <button
              className="expenses-button expenses-button--secondary"
              type="button"
              onClick={handleReceiptUpload}
            >
              <FiUpload />
              Upload
            </button>
            <button
              className="expenses-button expenses-button--primary"
              type="button"
              onClick={handleAddNew}
            >
              <FiPlus />
              Add expense
            </button>
          </div>
        </header>
        <p className="expenses-period">
          All expenses · Expense date · {currencyCode || "AUD"} · Saved / posted
          only · Drafts excluded
        </p>
        <section className="expenses-summary" aria-label="Expense summary">
          {loading ? (
            <CardSkeleton variant="stat" count={3} />
          ) : (
            <>
              <article>
                <h2>Recorded spend</h2>
                <strong>${formatCurrencyAUD(recordedSpend)}</strong>
                <p>
                  {totalRows} saved {totalRows === 1 ? "expense" : "expenses"}
                </p>
              </article>
              <article>
                <h2>GST recorded</h2>
                <strong>${formatCurrencyAUD(gstRecorded)}</strong>
                <p>Source values · Not GST claimable</p>
              </article>
              <article>
                <h2>Missing receipts</h2>
                <strong>{missingReceipts}</strong>
                <p>
                  {missingReceipts
                    ? "Evidence still needed"
                    : "All receipts attached"}
                </p>
              </article>
            </>
          )}
        </section>
        <nav className="expenses-tabs" aria-label="Expense states">
          <button
            className={activeTab === "saved" ? "is-active" : ""}
            onClick={() => setActiveTab("saved")}
            type="button"
          >
            Saved ({savedRows.length})
          </button>
          <button
            className={activeTab === "drafts" ? "is-active" : ""}
            onClick={() => setActiveTab("drafts")}
            type="button"
          >
            Drafts (
            {
              rows.filter(
                (row) => row.status?.toLowerCase() === "draft" || row.isDraft
              ).length
            }
            )
          </button>
          <button
            className={activeTab === "missing" ? "is-active" : ""}
            onClick={() => setActiveTab("missing")}
            type="button"
          >
            Missing receipt ({missingReceipts})
          </button>
        </nav>
        <section className="expenses-filters" aria-label="Expense filters">
          <div className="expenses-search">
            <FiSearch />
            <ClearableSearchInput
              aria-label="Search expenses"
              placeholder="Search supplier, expense reference or description"
              value={values.filterValues}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  filterValues: event.target.value,
                }))
              }
              onKeyUp={(event) => event.key === "Enter" && fetchExpenses()}
            />
          </div>
          <div className="expenses-filter-row">
            <div className="expenses-filter expenses-filter--category">
              <InvoiceSelect
                value={values.categoryCodeName}
                displayValue={selectedFilters.category ? undefined : "Categories"}
                onChange={(event) => {
                  setSelectedFilters((current) => ({ ...current, category: true }));
                  setValues((current) => ({
                    ...current,
                    categoryCodeName: event.target.value,
                    page: 1,
                  }));
                }}
                aria-label="Filter by category"
              >
                <option value="All">All</option>
                {itemCategoriesName.map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </InvoiceSelect>
            </div>
            <div className="expenses-filter expenses-filter--receipt">
              <InvoiceSelect
                value={registerFilters.receipt}
                displayValue={selectedFilters.receipt ? undefined : "Receipt status"}
                onChange={(event) => {
                  setSelectedFilters((current) => ({ ...current, receipt: true }));
                  setRegisterFilters((current) => ({
                    ...current,
                    receipt: event.target.value,
                  }));
                }}
                aria-label="Filter by receipt status"
              >
                <option value="All">All</option>
                <option value="Attached">Attached</option>
                <option value="Missing">Missing</option>
              </InvoiceSelect>
            </div>
            <div className="expenses-filter expenses-filter--payment">
              <InvoiceSelect
                value={registerFilters.payment}
                displayValue={selectedFilters.payment ? undefined : "Payment status"}
                onChange={(event) => {
                  setSelectedFilters((current) => ({ ...current, payment: true }));
                  setRegisterFilters((current) => ({
                    ...current,
                    payment: event.target.value,
                  }));
                }}
                aria-label="Filter by payment status"
              >
                <option value="All">All</option>
                <option value="Not confirmed">Not confirmed</option>
                <option value="Unpaid">Unpaid</option>
                <option value="Partially paid">Partially paid</option>
                <option value="Paid">Paid</option>
              </InvoiceSelect>
            </div>
            <div className="expenses-filter expenses-filter--date">
              <InvoiceSelect
                value={registerFilters.month}
                displayValue={selectedFilters.month ? expenseMonthLabel(registerFilters.month) : "Expense date"}
                onChange={(event) => {
                  setSelectedFilters((current) => ({ ...current, month: true }));
                  setRegisterFilters((current) => ({
                    ...current,
                    month: event.target.value,
                  }));
                }}
                aria-label="Filter by expense month"
              >
                <option value="">All</option>
                {expenseMonths.map((month) => (
                  <option value={month} key={month}>
                    {expenseMonthLabel(month)}
                  </option>
                ))}
              </InvoiceSelect>
            </div>
            {Object.values(selectedFilters).some(Boolean) && (
              <button
                className="expenses-clear"
                type="button"
                onClick={() => {
                  setSelectedFilters({});
                  setValues((current) => ({
                    ...current,
                    categoryCodeName: "All",
                    filterValues: "",
                    page: 1,
                  }));
                  setRegisterFilters({
                    receipt: "All",
                    payment: "All",
                    month: "",
                  });
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <p>
            Date range is based on expense date, not due date or payment date.
          </p>
        </section>
        <section className="expenses-register">
          <div className="expenses-register__heading">
            <strong>
              {displayedRowsCount} {displayedRowsCount === 1 ? "expense" : "expenses"}
            </strong>
            <span>Newest expense date first</span>
          </div>
          <div className="expenses-table-wrap" aria-busy={loading}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Category</th>
                  <th>Receipt</th>
                  <th>Payment</th>
                  <th>GST recorded</th>
                  <th>Total amount</th>
                  <th>Outstanding</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? loadingRows.map((row) => (
                      <tr
                        key={`loading-${row}`}
                        className="expenses-loading-row"
                      >
                        {[52, 112, 96, 62, 84, 72, 82, 72, 24].map(
                          (width, cell) => (
                            <td key={cell}>
                              <Skeleton
                                width={width}
                                height={cell === 8 ? 24 : 14}
                                radius={cell === 8 ? 8 : 6}
                              />
                            </td>
                          )
                        )}
                      </tr>
                    ))
                  : visibleRows.map((row) => {
                      const hasReceipt = Boolean(
                        row.uploadDocument || row.receipt || row.receiptFileName
                      );
                      return (
                        <tr key={row._id}>
                          <td data-label="Date">
                            {formatExpenseDate(row.expenseDate)}
                          </td>
                          <td data-label="Supplier">
                            {row.companyName || row.name || "—"}
                          </td>
                          <td data-label="Category">
                            {row.categoryCodeName || "—"}
                          </td>
                          <td data-label="Receipt">
                            <div className="expenses-receipt-state">
                              <span
                                className={`expenses-status ${
                                  hasReceipt ? "is-attached" : "is-missing"
                                }`}
                              >
                                {hasReceipt ? "Attached" : "Missing"}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  hasReceipt
                                    ? setReceiptViewer({
                                        name:
                                          row.uploadDocument ||
                                          row.receipt ||
                                          row.receiptFileName,
                                      })
                                    : navigate(
                                        `/expenses/editExpenses/${row._id}`
                                      )
                                }
                              >
                                {hasReceipt ? "View" : "Add receipt"}
                              </button>
                            </div>
                          </td>
                          <td data-label="Payment">
                            {row.paymentStatus || "Not confirmed"}
                          </td>
                          <td
                            data-label="GST recorded"
                            className="expenses-money"
                          >
                            ${formatCurrencyAUD(row.taxAmount)}
                          </td>
                          <td
                            data-label="Total amount"
                            className="expenses-money"
                          >
                            ${formatCurrencyAUD(row.totalAmount)}
                          </td>
                          <td
                            data-label="Outstanding"
                            className="expenses-money"
                          >
                            {row.paymentStatus === "Not confirmed"
                              ? "—"
                              : `$${formatCurrencyAUD(row.outstandingAmount)}`}
                          </td>
                          <td
                            data-label="Actions"
                            className="expenses-actions-cell"
                          >
                            <button
                              aria-label={`Actions for ${
                                row.companyName || row.name || "expense"
                              }`}
                              type="button"
                              className="listing-action-trigger"
                              aria-expanded={openMenu === row._id}
                              onClick={(event) => {
                                const isOpen = openMenu === row._id;
                                setOpenMenu(isOpen ? "" : row._id);
                                setMenuAnchorEl(
                                  isOpen ? null : event.currentTarget
                                );
                              }}
                            >
                              <FiMoreHorizontal />
                            </button>
                            {openMenu === row._id && (
                              <ListingActionMenu
                                anchorEl={menuAnchorEl}
                                label={`Actions for ${
                                  row.companyName || row.name || "expense"
                                }`}
                                onClose={() => {
                                  setOpenMenu("");
                                  setMenuAnchorEl(null);
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/expenses/${row._id}`)
                                  }
                                >
                                  <FiEye />
                                  View details
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/expenses/editExpenses/${row._id}`
                                    )
                                  }
                                >
                                  <FiEdit2 />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  onClick={() => requestDelete(row._id)}
                                >
                                  <FiTrash2 />
                                  Delete
                                </button>
                              </ListingActionMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
            {!loading && !visibleRows.length && (
              <div className="expenses-empty">No expenses match this view.</div>
            )}
          </div>
          <footer className="expenses-pagination">
            <span>
              Showing {visibleRows.length ? (values.page - 1) * perPage + 1 : 0}
              –{Math.min(values.page * perPage, totalRows)} of {totalRows}{" "}
              expenses
            </span>
            <div>
              <button
                aria-label="Previous page"
                disabled={values.page <= 1}
                onClick={() => changePage(values.page - 1)}
                type="button"
              >
                <FiChevronLeft />
              </button>
              <button
                aria-label="Next page"
                disabled={values.page >= pageCount}
                onClick={() => changePage(values.page + 1)}
                type="button"
              >
                <FiChevronRight />
              </button>
            </div>
          </footer>
        </section>
        <p className="expenses-disclaimer">
          Recorded GST is not an eligibility assessment or a confirmed GST
          report inclusion.
        </p>
      </main>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        handleDelete={handleDelete}
        handleCancel={() => setDeleteDialogOpen(false)}
        closeModalClick={() => setDeleteDialogOpen(false)}
        message=""
      />
      {receiptViewer && (
        <div
          className="expense-viewer document-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Receipt viewer"
        >
          <div>
            <header>
              <strong>{receiptViewer.name}</strong>
              <button type="button" onClick={() => setReceiptViewer(null)}>
                Close
              </button>
            </header>
            <iframe
              title="Receipt viewer"
              src={`${
                appConstants.baseURL
              }expenses/viewDocument?fileName=${encodeURIComponent(
                receiptViewer.name
              )}`}
            />
          </div>
        </div>
      )}
      <BillingModal
        open={showBillingModal}
        handleConfirm={() => navigate("/subscriptions")}
        handleCancel={() => setShowBillingModal(false)}
        closeModalClick={() => setShowBillingModal(false)}
        message={genericMessage.billingMsg}
        title={genericMessage.noCreditAvailableTitle}
      />
    </DashboardLayout>
  );
}
export default Expenses;
